import { surferHooks } from "components/Record"
import { statuses } from "keys"
import { pick } from "lodash"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { actions } from "store"
import { continueMarkingWordsFetch, startMarkingWordsFetch } from "store/middlewares/markingWords.crud"
import { markingWordsHooks } from ".."
import qc from "keys/queryClient"
import { toast } from "react-toastify"
import { toastConfig } from "config"
import { useNavigate } from "react-router-dom"
import { app } from "router/routes"

const useStartMarkingWords = () => {
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    const { assessment } = useSelector(state => state.assessment)
    const { isCloseSttoper } = useSelector(state => state.markingWords)

    const isTraining = markingWordsHooks.useIsTraining()
    const backToMarking = markingWordsHooks.useBackToConfirmation()
    const onPlay = surferHooks.useOnPlay()

    const { mutateAsync: startMarkingWordsRQ, isPending: startMarkingWordsLoading } = useMutation({
        mutationFn: startMarkingWordsFetch,
        onError: ({ response }) => {
            if (response.data.IN_PROGRESS_ERROR) {
                toast.error('המבדק כבר התחיל סימון ע"י בודקת אחרת....', toastConfig)

                if (!isTraining)
                    navigate(app.markingWordsAssessments)

                else
                    navigate(app.markingTrainingWordsAssessments)

            }
        }
    })

    const { mutateAsync: continueMarkingWordsRQ, isPending: continueMarkingWordsLoading } = useMutation({
        mutationFn: continueMarkingWordsFetch
    })

    const { save: saveWords, loading: saveLoading } = markingWordsHooks.useSave()

    const startMarkingWords = async (nAssessment) => {
        const res = await startMarkingWordsRQ({ assessment: nAssessment, isTrainingAssessment: isTraining })
        await saveWords()
        await queryClient.invalidateQueries({ queryKey: [qc.assessmentAndWords] })
        const assessmentDTO = pick(res?.data, ["status", "sub_status"])
        dispatch(actions.updateAssessment(assessmentDTO))
        dispatch(actions.setIsCloseSttoper(false))
        if (assessment.audio_file)
            onPlay()
    }

    const continueMarkWords = async () => {
        await continueMarkingWordsRQ({ assessment, isTrainingAssessment: isTraining })
        dispatch(actions.setIsCloseSttoper(false))
        if (assessment.audio_file)
            onPlay()
    }

    const markWords = {
        [statuses.inProgress]: {
            [statuses.wordsInProgress]: {
                label: "המשך סימון",
                action: continueMarkWords,
                loading: continueMarkingWordsLoading,
                disabled:
                    // startMarkingWordsData && 
                    !isCloseSttoper
            },
            [statuses.wordsComplete]: {
                label: "החזר לסימון",
                action: backToMarking.action,
                loading: backToMarking.loading,
                disabled: backToMarking.loading
            },
            [statuses.recheckPending]: {
                label: "החזר לסימון",
                action: backToMarking.action,
                loading: backToMarking.loading,
                disabled: backToMarking.loading
            },
            [statuses.aiPending]: {
                label: "החזר לסימון",
                action: backToMarking.action,
                loading: backToMarking.loading,
                disabled: backToMarking.loading
            }
        },
        [statuses.uploaded]: {
            "default": {
                label: "התחל סימון",
                action: startMarkingWords,
                loading: startMarkingWordsLoading || saveLoading,
                disabled: startMarkingWordsLoading || saveLoading
            }
        },
        [statuses.backToMarking]: {
            "default": {
                label: "התחל סימון",
                action: startMarkingWords,
                loading: startMarkingWordsLoading,
                disabled: startMarkingWordsLoading
            }
        },
        [statuses.redo]: {
            "default": {
                label: "החזר לסימון",
                action: backToMarking.action,
                loading: backToMarking.loading,
                disabled: backToMarking.loading
            },
            [statuses.recheckPending]: {
                label: "החזר לסימון",
                action: backToMarking.action,
                loading: backToMarking.loading,
                disabled: backToMarking.loading
            }
        }
    }

    const markWordsValue = markWords[assessment.status]?.[assessment.sub_status || "default"]

    const isRedoStatus = assessment.status == statuses.redo

    const startMark = {
        label: markWordsValue?.label || "התחל סימון",
        color: "secondary",
        action: () => {
            markWordsValue?.action(assessment)
        },
        disabled: markWordsValue?.disabled || !markWordsValue,
        component: 'button',
        width: "200px",
        disabledColor: "gray2",
        loading: markWordsValue?.loading,
        hide: markWordsValue?.hide
    }

    const redoItem = {
        component: "custom",
        customComponent: () => <div className="redo-message">
            <span className="bold">{"הוחזר לביצוע חוזר "}</span>
            {assessment.status_type && ` - ${assessment.status_type}`}
        </div>
    }

    const items = isRedoStatus ? [startMark, redoItem] : [startMark]
    return items
}

export default useStartMarkingWords